import { Diamond } from 'lucide-react';
import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type BoardView, type TaskView } from '@apm/contracts';
import { displayDate } from '../../../lib/date';
import { cn } from '../../../lib/utils';

/** Một mốc trên dải, kèm cờ "đã đạt" suy từ cột nó đang đứng. */
export interface StripMilestone {
  readonly task: TaskView;
  readonly done: boolean;
}

/**
 * Mốc của board đang xem, theo ngày — sớm trước, chưa có ngày xếp cuối.
 * "Đã đạt" là mốc đứng ở cột loại `done`.
 */
export function milestonesOf(view: BoardView): StripMilestone[] {
  return view.columns
    .flatMap((column) =>
      column.tasks
        .filter((task) => task.kind === 'milestone')
        .map((task) => ({ task, done: column.status.category === 'done' })),
    )
    .sort((a, b) => (a.task.dueDate ?? '9999').localeCompare(b.task.dueDate ?? '9999'));
}

/**
 * Dải "Mốc" phía trên board — người dùng chốt 2026-09-25.
 *
 * Mốc không có thẻ trên Kanban: nó là một điểm trên trục thời gian, không phải
 * việc chảy qua các cột. Nhưng ẩn hẳn thì người chỉ xem Board không biết sprint
 * này phải chạm mốc nào — nên nó đứng ở đây, một hàng chip, bấm vào thì mở chi
 * tiết như một thẻ.
 *
 * ## Điện thoại: một hàng cuộn ngang
 *
 * Xuống dòng thì ba mốc đã thành ba hàng ở 390px — mốc đã đạt (gạch ngang,
 * không còn gì để làm) đứng trước và đẩy board xuống (người dùng yêu cầu
 * 2026-10-07). Nên ở `max-sm` dải giữ một hàng, cuộn ngang, và mở ra đã cuộn
 * sẵn tới **mốc chưa đạt đầu tiên**: mốc đã qua vẫn kéo ngược lại xem được,
 * nhưng thứ hiện ra trước là thứ sprint còn phải chạm.
 *
 * ⚠️ Không nhận asOf: thuần trình bày, nhận dữ liệu board đã lấy sẵn.
 */
export function MilestoneStrip({
  milestones,
  today,
  onOpen,
}: {
  readonly milestones: readonly StripMilestone[];
  /** `YYYY-MM-DD` — chốt một lần cho cả lần vẽ board. */
  readonly today: string;
  readonly onOpen: (taskId: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement | null>(null);
  const nextId = milestones.find((milestone) => !milestone.done)?.task.id ?? null;

  // Cuộn **khung dải**, không dùng `scrollIntoView` — nó cuộn luôn cả trang
  // theo chiều dọc. Màn rộng không tràn nên `scrollLeft` không đổi gì.
  useLayoutEffect(() => {
    const list = listRef.current;
    const next = list?.querySelector<HTMLElement>('[data-next="true"]');
    if (!list || !next) return;
    list.scrollLeft += next.getBoundingClientRect().left - list.getBoundingClientRect().left;
  }, [nextId]);

  if (milestones.length === 0) return null;

  return (
    // Nhãn đứng **ngoài** khung cuộn: cuộn tới mốc kế tiếp không cuốn mất chữ
    // "Mốc", và hàng chip vẫn bắt đầu thẳng mép sau nó.
    <div className="mt-2 flex items-center gap-1.5 text-xs">
      <span className="shrink-0 self-start py-0.5 font-medium text-muted-foreground max-sm:self-center">
        {t('board.milestones')}
      </span>
      <div
        ref={listRef}
        role="list"
        aria-label={t('board.milestones')}
        className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:overscroll-x-contain max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden"
      >
        {milestones.map(({ task, done }) => {
          const late = !done && task.dueDate !== null && task.dueDate < today;
          return (
            <button
              key={task.id}
              type="button"
              role="listitem"
              data-next={task.id === nextId}
              onClick={() => onOpen(task.id)}
              title={t(
                done ? 'board.milestoneDone' : late ? 'board.milestoneLate' : 'board.milestoneOpen',
              )}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 hover:bg-muted',
                done && 'border-border text-muted-foreground',
                late && 'border-destructive/50 text-destructive',
                !done && !late && 'border-primary/40',
              )}
            >
              <Diamond aria-hidden className={cn('size-3', done ? 'fill-current' : 'fill-none')} />
              <span
                className={cn(
                  'max-w-[16rem] truncate max-sm:max-w-[11rem]',
                  done && 'line-through',
                )}
              >
                {task.title}
              </span>
              <span className="tabular-nums">{displayDate(task.dueDate)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
